import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AlertProps } from '@bancobarigui/barihaus';

interface NotifyMessageParams {
  message: string;
  type: AlertProps['type'];
  title?: string;
  duration?: number; 
}

interface QueuedAlert extends NotifyMessageParams {
  id: number;
}

export type AlertContextType = {
  message: string | null;
  type: AlertProps['type'] | null;
  title: string | null;
  notify: (params: NotifyMessageParams) => void;
  dismiss: () => void;
};

const AlertContext = createContext<AlertContextType>({} as AlertContextType);

let alertIdCounter = 0;

export function AlertProvider({ children }: PropsWithChildren) {
  const [currentAlert, setCurrentAlert] = useState<QueuedAlert | null>(null);
  const alertQueue = useRef<QueuedAlert[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const processQueue = useCallback(() => {
    if (alertQueue.current.length > 0 && !currentAlert) {
      const nextAlert = alertQueue.current.shift()!;
      setCurrentAlert(nextAlert);
    }
  }, [currentAlert]);

  const dismiss = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setCurrentAlert(null);
  }, []);

  const notify = useCallback(({ message, type, title, duration = 3000 }: NotifyMessageParams) => {
    const newAlert: QueuedAlert = {
      id: ++alertIdCounter,
      message,
      type,
      title,
      duration,
    };

    if (!currentAlert) {
      setCurrentAlert(newAlert);
    } else {
      if (alertQueue.current.length < 5) {
        alertQueue.current.push(newAlert);
      }
    }
  }, [currentAlert]);

  useEffect(() => {
    if (currentAlert) {
      timeoutRef.current = setTimeout(() => {
        setCurrentAlert(null);
      }, currentAlert.duration || 3000);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [currentAlert]);

  useEffect(() => {
    if (!currentAlert) {
      const timer = setTimeout(processQueue, 300);
      return () => clearTimeout(timer);
    }
  }, [currentAlert, processQueue]);

  return (
    <AlertContext.Provider
      value={{
        message: currentAlert?.message || null,
        type: currentAlert?.type || null,
        title: currentAlert?.title || null,
        notify,
        dismiss,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
}

export function useAlertContext() {
  return useContext(AlertContext);
}
